import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import type { FilterSelectProps } from '../FilterPanel.types';

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M2 7l4 4 6-8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function FilterSelectComponent({ config, value, onChange, compact }: FilterSelectProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = config.options?.find(opt => opt.value === value);

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      calculatePosition();
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', () => setIsOpen(false), true);
      return () => {
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', () => setIsOpen(false), true);
      };
    }
  }, [isOpen, calculatePosition]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue === value ? null : optionValue);
    setIsOpen(false);
  };

  const dropdown = isOpen && config.options && (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: position.width,
        maxHeight: '240px',
        overflowY: 'auto',
        background: 'var(--color-bg-elevated)',
        border: '1px solid var(--color-border-default)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)',
        zIndex: 9999,
        animation: 'scaleIn var(--transition-fast) forwards'
      }}
    >
      {config.options.map(option => (
        <button
          key={option.value}
          onClick={() => handleSelect(option.value)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: compact ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)',
            fontSize: 'var(--text-sm)',
            textAlign: 'left',
            border: 'none',
            background: option.value === value ? 'var(--color-primary-50)' : 'transparent',
            color: 'var(--color-text-primary)',
            cursor: 'pointer',
            transition: 'background var(--transition-fast)'
          }}
          onMouseEnter={(e) => {
            if (option.value !== value) {
              e.currentTarget.style.background = 'var(--color-hover-overlay)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = option.value === value ? 'var(--color-primary-50)' : 'transparent';
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            {option.label}
            {option.count !== undefined && (
              <span style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-tertiary)',
                padding: '0 var(--space-1)',
                background: 'var(--color-neutral-100)',
                borderRadius: 'var(--radius-full)'
              }}>
                {option.count}
              </span>
            )}
          </span>
          {option.value === value && (
            <span style={{ color: 'var(--color-primary)' }}>
              <CheckIcon />
            </span>
          )}
        </button>
      ))}
    </div>
  );

  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: 'var(--text-xs)',
        fontWeight: 500,
        color: 'var(--color-text-secondary)',
        marginBottom: 'var(--space-1)'
      }}>
        {config.label}
      </label>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: compact ? 'var(--space-2) var(--space-3)' : 'var(--space-2) var(--space-3)',
          fontSize: 'var(--text-sm)',
          fontFamily: 'var(--font-body)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border-default)',
          background: 'var(--color-bg-elevated)',
          color: selectedOption ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
          cursor: 'pointer',
          transition: 'all var(--transition-fast)'
        }}
      >
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {selectedOption?.label || config.placeholder || 'Seçiniz...'}
        </span>
        <span style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform var(--transition-fast)',
          flexShrink: 0,
          marginLeft: 'var(--space-2)'
        }}>
          <ChevronDownIcon />
        </span>
      </button>
      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
}

export const FilterSelect = memo(FilterSelectComponent);
